import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useAudioPlayerStore = defineStore(
    'audioPlayer',
    () => {
        const playbackRate = ref(1);
        const autoScrollEnabled = ref(true);

        // Pending seek requests (in seconds) keyed by interaction id. Set by other
        // components (e.g. a transcription message's "listen from here" button) and
        // consumed/reset by the matching interaction's audio player. Keyed so that
        // multiple players on the same page each only react to their own seek.
        const pendingSeeks = ref(new Map<string, number>());

        const seekTo = (interactionId: string, seconds: number) => {
            pendingSeeks.value.set(interactionId, Math.max(0, seconds));
        };

        const clearSeek = (interactionId: string) => {
            pendingSeeks.value.delete(interactionId);
        };

        // Audio players currently mounted with playable audio, counted per
        // interaction id. A "listen from here" button only makes sense to show when
        // that interaction's player is on the page to consume the seek. Counted
        // (rather than a plain Set) to stay correct if the same interaction is
        // rendered more than once at the same time (e.g. expanded table rows).
        const playerCounts = ref(new Map<string, number>());

        const hasPlayerFor = (interactionId: string) => playerCounts.value.has(interactionId);

        const registerPlayer = (interactionId: string) => {
            playerCounts.value.set(interactionId, (playerCounts.value.get(interactionId) ?? 0) + 1);
        };

        const unregisterPlayer = (interactionId: string) => {
            const next = (playerCounts.value.get(interactionId) ?? 0) - 1;

            if (next <= 0) {
                playerCounts.value.delete(interactionId);
                pendingSeeks.value.delete(interactionId);
            } else {
                playerCounts.value.set(interactionId, next);
            }
        };

        return {
            playbackRate,
            autoScrollEnabled,
            pendingSeeks,
            seekTo,
            clearSeek,
            hasPlayerFor,
            registerPlayer,
            unregisterPlayer,
        };
    },
    {
        persist: {
            storage: sessionStorage,
            pick: ['playbackRate', 'autoScrollEnabled'],
        },
    }
);
